<script setup>
import { ref } from 'vue';
import { updateUserData } from '../../utils/api';
import StatusMessage from '../StatusMessage';
import { default as t } from '../../utils/translationStrings';

// Props.
const props = defineProps({
  user: {
    type: Object,
    required: true,
    default: () => {},
  },
});

// Data.
const showMessage = ref(false);
const message = ref('');
const messageType = ref('success');
const abortButton = ref(null);

// Model value.
const modelData = ref({
  firstName: props.user?.defaultBillingAddress?.firstName,
  lastName: props.user?.defaultBillingAddress?.lastName,
  company: props.user?.defaultBillingAddress?.company,
  countryId: props.user?.defaultBillingAddress?.countryId,
  street: props.user?.defaultBillingAddress?.street,
  zipcode: props.user?.defaultBillingAddress?.zipcode,
  city: props.user?.defaultBillingAddress?.city,
  phoneNumber: props.user?.defaultBillingAddress?.phoneNumber,
});

// Events.
const emit = defineEmits(['addressUpdated', 'abort']);

// Methods.
const submit = async () => {
  showMessage.value = true;

  try {
    const { data } = await updateUserData({
      type: 'address',
      addressId: props.user?.defaultBillingAddressId,
      userId: props.user?.id,
      newAddress: modelData.value,
    });

    if (data.shopResponse.id) {
      messageType.value = 'success';
      message.value = t.dashboardMyData.addressUpdated;
      abortButton.value.textContent = t.dashboardMyData.closeButton;

      // Let the parent know data changed, so that it requests a re-fetch.
      emit('addressUpdated', data.shopResponse);
    }
  } catch (error) {
    messageType.value = 'error';
    message.value = t.dashboardMyData.addressUpdateFailed;
  }
};
</script>

<template>
  <!-- Use Drupal classes for styling purposes. -->
  <form @submit.prevent="submit" class="webform-submission-form">
    <StatusMessage
      v-if="showMessage"
      v-model="showMessage"
      :message-type="messageType"
      :message-text="message" />
    <div class="form-wrapper">
      <div class="form-item">
        <label for="first-name" class="form-required">{{
          t.dashboardMyData.firstName
        }}</label>
        <input
          id="first-name"
          v-model="modelData.firstName"
          type="text"
          required />
      </div>
      <div class="form-item">
        <label for="last-name" class="form-required">{{
          t.dashboardMyData.lastName
        }}</label>
        <input
          id="last-name"
          v-model="modelData.lastName"
          type="text"
          required />
      </div>
    </div>
    <div class="form-item">
      <label for="company" class="form-required">{{
        t.dashboardMyData.company
      }}</label>
      <input id="company" v-model="modelData.company" type="text" required />
    </div>
    <div class="form-item">
      <label for="street" class="form-required">{{
        t.dashboardMyData.street
      }}</label>
      <input id="street" v-model="modelData.street" type="text" required />
    </div>
    <div class="form-wrapper">
      <div class="form-item">
        <label for="city" class="form-required">{{
          t.dashboardMyData.city
        }}</label>
        <input id="city" v-model="modelData.city" type="text" required />
      </div>
      <div class="form-item">
        <label for="zipcode" class="form-required">{{
          t.dashboardMyData.zipcode
        }}</label>
        <input id="zipcode" v-model="modelData.zipcode" type="text" required />
      </div>
    </div>
    <div class="form-item">
      <label for="phone-number">{{ t.dashboardMyData.phoneNumber }}</label>
      <input id="phone-number" v-model="modelData.phoneNumber" type="text" />
    </div>
    <div class="form-actions">
      <button type="submit" class="button">
        {{ t.dashboardMyData.saveButton }}
      </button>
      <button
        ref="abortButton"
        @click="emit('abort')"
        class="button button--border button--no-icon">
        {{ t.dashboardMyData.abortButton }}
      </button>
    </div>
  </form>
</template>
